import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  CreditCard, 
  Clock, 
  Gift, 
  CheckCircle, 
  AlertCircle,
  ArrowLeft,
  Loader2,
  Sparkles,
  Banknote,
  Calendar,
  Info,
  XCircle
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { AiAvatar } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDLE_TIMEOUT = 180;

const PARKING_MESSAGES = {
  welcome: "Welcome to pay for parking. Enter your vehicle registration to check your parking fee. If you've shopped with a Local Connect partner today, your discount will be applied automatically.",
  calculating: "Let me calculate your parking fee...",
  entitlement: "Great news! You have a Local Connect discount. Your first 3 hours are free from your qualifying partner visit today.",
  payment: "Your parking fee is ready. Please proceed to payment.",
  free: "Congratulations! Your parking is completely free today thanks to your Local Connect discount.",
  success: "Payment complete! Your vehicle is now authorised to exit. Thank you for visiting.",
  noDiscount: "Your parking fee is ready. No Local Connect discount available today. Shop with our partner retailers to earn 3 hours free parking on your next visit.",
  error: "Sorry, I couldn't find an active parking session for this vehicle. Please check your registration number."
};

export default function KioskPayParking() {
  const navigate = useNavigate();
  const [vehicleReg, setVehicleReg] = useState("");
  const [loading, setLoading] = useState(false);
  const [tariffData, setTariffData] = useState(null);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState(PARKING_MESSAGES.welcome);
  const idleTimerRef = useRef(null);

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  const formatVehicleReg = (value) => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  };

  const handleVehicleRegChange = (e) => {
    const formatted = formatVehicleReg(e.target.value);
    if (formatted.length <= 8) {
      setVehicleReg(formatted);
    }
  };

  const handleCheckTariff = async () => {
    if (vehicleReg.length < 5) {
      setError("Please enter a valid vehicle registration");
      return;
    }

    setLoading(true);
    setError(null);
    setTariffData(null);
    setAvatarMessage(PARKING_MESSAGES.calculating);

    try {
      const response = await axios.get(`${API}/parking/session/${vehicleReg}/calculate`);
      setTariffData(response.data);
      
      if (response.data.has_entitlement && response.data.entitlement_discount > 0) {
        if (response.data.amount_due === 0) {
          setAvatarMessage(PARKING_MESSAGES.free);
        } else {
          setAvatarMessage(PARKING_MESSAGES.entitlement);
        }
      } else if (response.data.is_grace_period) {
        setAvatarMessage("You're within the 15-minute grace period. No charge today!");
      } else {
        setAvatarMessage(PARKING_MESSAGES.noDiscount);
      }
    } catch (err) {
      console.error("Tariff check error:", err);
      if (err.response?.status === 404) {
        setError("No active parking session found for this vehicle. Please ensure your vehicle entry was recorded at the barrier.");
        setAvatarMessage(PARKING_MESSAGES.error);
      } else {
        setError("Unable to calculate parking fee. Please try again or seek assistance.");
        setAvatarMessage(PARKING_MESSAGES.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    // For £0.00 amount (free parking with entitlement), use the free exit endpoint
    if (tariffData.amount_due === 0) {
      setLoading(true);
      try {
        // Call the free exit authorization endpoint (no payment required)
        await axios.post(`${API}/parking/session/${vehicleReg}/authorize-free-exit`, {
          session_id: tariffData.session_id,
          apply_entitlement: true
        });
        
        setPaymentSuccess(true);
        setAvatarMessage(PARKING_MESSAGES.free);
        
        setTimeout(() => {
          navigate("/kiosk");
        }, 10000);
      } catch (err) {
        console.error("Free exit authorization error:", err);
        setError("Unable to authorize free exit. Please seek assistance.");
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // For paid parking: redirect to Stripe checkout
    setLoading(true);
    setAvatarMessage("Redirecting to secure payment...");
    
    try {
      // Call POST /api/payment/create to get Stripe checkout URL
      const response = await axios.post(`${API}/payment/create`, {
        session_id: tariffData.session_id,
        vehicle_registration: vehicleReg,
        amount: tariffData.amount_due,
        site_id: tariffData.site_id || "site_001",
        apply_entitlement: true
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Payment creation error:", err);
      const errorDetail = err.response?.data?.detail || "Payment failed. Please try again or seek assistance.";
      setError(errorDetail);
      setAvatarMessage(PARKING_MESSAGES.error);
      setLoading(false);
    }
    // Note: setLoading(false) not called on success because we're redirecting
  };

  const formatDisplayReg = (reg) => {
    if (reg.length <= 4) return reg;
    return `${reg.slice(0, 4)} ${reg.slice(4)}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F0FDF4 100%)' }} data-testid="kiosk-pay-parking">
      {/* Header */}
      <header className="flex items-center justify-between p-6 md:p-8">
        <ParkWiseLogo size="lg" />
        
        <button
          onClick={() => navigate("/kiosk")}
          className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center px-6 md:px-12 pb-12">
        <div className="w-full max-w-2xl">
          {/* Avatar */}
          <div className="flex justify-center mb-8">
            <AiAvatar 
              message={avatarMessage} 
              size="medium"
              autoSpeak={true}
            />
          </div>

          {/* Success State */}
          {paymentSuccess ? (
            <div className="animate-fade-in text-center">
              <div 
                className="w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.2) 100%)' }}
              >
                <CheckCircle className="w-16 h-16 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment Complete!
              </h2>
              <p className="text-xl text-slate-600 mb-2">
                Vehicle <span className="font-mono font-bold">{formatDisplayReg(vehicleReg)}</span> is authorised to exit.
              </p>
              <p className="text-slate-500">
                Returning to home screen in 10 seconds...
              </p>
            </div>
          ) : !tariffData ? (
            /* Vehicle Registration Entry */
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Enter Your Registration
                </h2>
                <p className="text-lg text-slate-600">
                  We'll check for any Local Connect free parking discount
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg border border-slate-200 mb-6">
                <Input
                  type="text"
                  value={formatDisplayReg(vehicleReg)}
                  onChange={handleVehicleRegChange}
                  placeholder="AB12 CDE"
                  className="text-center text-4xl font-mono font-bold tracking-wider h-20 border-2 border-slate-300 focus:border-blue-500 rounded-2xl"
                  data-testid="vehicle-reg-input"
                />
                
                {error && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700">{error}</p>
                  </div>
                )}
              </div>

              <Button
                onClick={handleCheckTariff}
                disabled={loading || vehicleReg.length < 5}
                className="w-full h-16 text-xl font-semibold rounded-2xl"
                style={{ background: 'linear-gradient(135deg, #2563EB 0%, #10B981 100%)' }}
                data-testid="check-tariff-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-6 h-6 mr-2" />
                    Check Parking Fee
                  </>
                )}
              </Button>

              {/* Info about Local Connect */}
              <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-purple-900 font-medium">Local Connect - 3 Hours Free Parking</p>
                    <p className="text-purple-700 text-sm mt-1">
                      Shop with our Local Connect partner retailers and get your first 3 hours free. 
                      Valid same day only.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Tariff Display */
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <p className="text-sm text-slate-500 mb-1">Vehicle Registration</p>
                <p className="text-4xl font-mono font-bold text-slate-900 tracking-wider">
                  {formatDisplayReg(vehicleReg)}
                </p>
              </div>

              {/* Parking Summary Card */}
              <div className="bg-white rounded-3xl shadow-lg border border-slate-200 overflow-hidden mb-6">
                {/* Entry Time */}
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Entry Time</p>
                      <p className="font-semibold text-slate-900">
                        {formatTime(tariffData.entry_time)} · {formatDate(tariffData.entry_time)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Duration */}
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Parking Duration</p>
                      <p className="font-semibold text-slate-900">{tariffData.duration_display}</p>
                    </div>
                  </div>
                </div>

                {/* Standard Rate */}
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Banknote className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Standard Tariff</p>
                      <p className="text-xs text-slate-400">@ £{tariffData.hourly_rate?.toFixed(2) || '2.00'}/hour</p>
                    </div>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    £{tariffData.base_tariff.toFixed(2)}
                  </span>
                </div>

                {/* Local Connect Discount (if available) */}
                {tariffData.has_entitlement && tariffData.entitlement_discount > 0 ? (
                  <div className="p-5 border-b border-slate-100" style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-purple-900">Local Connect Discount Applied</p>
                          <p className="text-sm text-purple-700">
                            First {tariffData.free_hours || 3} hours free from qualifying partner visit
                          </p>
                          <p className="text-xs text-purple-600 mt-1">Valid same day only</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-green-600">
                        -£{tariffData.entitlement_discount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  /* No discount available */
                  <div className="p-5 border-b border-slate-100 bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                        <XCircle className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">No Local Connect Discount Available</p>
                        <p className="text-sm text-slate-500">
                          Shop with our Local Connect partners to earn 3 hours free parking next time
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="p-6 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-slate-900">Amount Due</span>
                    <span 
                      className="text-4xl font-bold"
                      style={{ 
                        background: tariffData.amount_due === 0 
                          ? 'linear-gradient(135deg, #10B981 0%, #34D399 100%)' 
                          : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      £{tariffData.amount_due.toFixed(2)}
                    </span>
                  </div>
                  {tariffData.amount_due === 0 && tariffData.has_entitlement && (
                    <p className="text-green-600 text-sm mt-2 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Your parking is completely free today!
                    </p>
                  )}
                </div>
              </div>

              {/* Savings Summary */}
              {tariffData.has_entitlement && tariffData.entitlement_discount > 0 && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-5 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-green-800 font-bold text-lg">
                        You're saving £{tariffData.entitlement_discount.toFixed(2)} today!
                      </p>
                      <p className="text-green-700 text-sm">
                        Thanks to your Local Connect partner visit
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4">
                <Button
                  onClick={() => {
                    setTariffData(null);
                    setVehicleReg("");
                    setError(null);
                    setAvatarMessage(PARKING_MESSAGES.welcome);
                  }}
                  variant="outline"
                  className="flex-1 h-16 text-lg font-semibold rounded-2xl border-2"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Start Over
                </Button>

                <Button
                  onClick={handlePayment}
                  disabled={loading}
                  className="flex-1 h-16 text-xl font-semibold rounded-2xl text-white"
                  style={{ 
                    background: tariffData.amount_due === 0 
                      ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' 
                      : 'linear-gradient(135deg, #2563EB 0%, #10B981 100%)' 
                  }}
                  data-testid="pay-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : tariffData.amount_due === 0 ? (
                    <>
                      <CheckCircle className="w-6 h-6 mr-2" />
                      Exit Free
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-6 h-6 mr-2" />
                      Pay £{tariffData.amount_due.toFixed(2)}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-4 border-t border-slate-200 bg-white/50 backdrop-blur-sm">
        <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
          <Sparkles className="w-4 h-4" />
          <span>Shop with Local Connect partners to earn 3 hours free parking</span>
        </div>
      </footer>
    </div>
  );
}
